import { forwardRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHand,
  faRulerHorizontal,
  faRulerVertical,
  faShoePrints,
  faWeightHanging,
} from "@fortawesome/free-solid-svg-icons";
import { getScoreColor } from "../athlete-progress-bar";
import { getStateAbbreviation } from "../../../utils/stateAbbreviations";

const EMPTY_VALUE = "—";
const QR_CODE_SIZE = 80;
const SCORE_RING_SIZE = 76;
const SCORE_RING_STROKE = 4;
const SCORE_RING_COLOR = "#2563EB";

const isPresent = (value) => value !== undefined && value !== null;

const orDash = (value, format) =>
  isPresent(value) ? format(value) : EMPTY_VALUE;

// Measurements, formatted like athlete-measurements-list-athletic.jsx and
// athlete-measurements-list-position.jsx (weight).
const formatLength = (item) =>
  isPresent(item?.feet) || isPresent(item?.inches)
    ? `${item.feet ?? 0}' ${item.inches ?? 0}''`
    : EMPTY_VALUE;

const MEASUREMENT_TILES = [
  {
    key: "weight",
    title: "Weight",
    icon: faWeightHanging,
    format: (item) => orDash(item.value, (v) => `${v} lbs`),
  },
  {
    key: "height",
    title: "Height",
    icon: faRulerVertical,
    format: formatLength,
  },
  {
    key: "wingspan",
    title: "Wingspan",
    icon: faRulerHorizontal,
    format: formatLength,
  },
  {
    key: "hand",
    title: "Hand",
    icon: faHand,
    format: (item) => orDash(item.value, (v) => `${v}''`),
  },
  {
    key: "foot",
    title: "Foot",
    icon: faShoePrints,
    format: (item) => orDash(item.value, (v) => `${v}`),
  },
];

// Drills, formatted like athlete-combine-list-results.jsx: jumps get an inch
// mark, bench press shows value × reps, everything else is the raw value.
const plainValue = (item) => orDash(item.value, (v) => `${v}`);
const jumpValue = (item) => orDash(item.value, (v) => `${v}"`);

const DRILL_TILES = [
  { key: "tenYardSplit", title: "10 Yard Split", format: plainValue },
  { key: "twentyYardSplit", title: "20 Yard Split", format: plainValue },
  { key: "fortyYardDash", title: "40 Yard Dash", format: plainValue },
  {
    key: "benchPress",
    title: "Bench Press",
    format: (item) => orDash(item.value, (v) => `${v} × ${item.reps ?? 0}`),
  },
  { key: "verticalJump", title: "Vertical Jump", format: jumpValue },
  { key: "broadJump", title: "Broad Jump", format: jumpValue },
  { key: "twentyYardShuttle", title: "20-Yard Shuttle", format: plainValue },
  { key: "lDrill", title: "Three-Cone Drill", format: plainValue },
];

function formatSchoolLocation({ school, state, graduationYear }) {
  const place = [school, getStateAbbreviation(state)]
    .filter(Boolean)
    .join(", ");
  const year = isPresent(graduationYear) ? `(${graduationYear})` : "";
  return [place, year].filter(Boolean).join(" ");
}

// Static (non-animated) bar so html-to-image never captures it mid-animation.
// Scores are shown the way the dashboard shows them: measurements are rounded
// (show-measurements.jsx), drill scores are truncated with parseInt
// (athlete-combine-list-results.jsx).
function ScoreBar({ score, toWholeScore }) {
  const rounded = toWholeScore(Number(score));
  if (!Number.isFinite(rounded)) return null;
  const width = Math.min(Math.max(rounded, 0), 100);

  return (
    <div className="mt-1 flex items-center gap-1">
      <div className="h-[3px] flex-1 overflow-hidden rounded-full bg-white/20">
        <div
          className="h-[3px] rounded-full"
          style={{
            width: `${width}%`,
            backgroundColor: getScoreColor(rounded),
          }}
        />
      </div>
      <span className="text-[8px] leading-none text-gray-300">{rounded}</span>
    </div>
  );
}

// Static ring with the score and caption inside, as in the Figma.
function PositionScoreRing({ score }) {
  const value = Math.min(Math.max(Math.round(Number(score)) || 0, 0), 100);
  const radius = (SCORE_RING_SIZE - SCORE_RING_STROKE) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = SCORE_RING_SIZE / 2;

  return (
    <div
      className="relative shrink-0"
      style={{ width: SCORE_RING_SIZE, height: SCORE_RING_SIZE }}
    >
      <svg
        width={SCORE_RING_SIZE}
        height={SCORE_RING_SIZE}
        viewBox={`0 0 ${SCORE_RING_SIZE} ${SCORE_RING_SIZE}`}
        aria-hidden="true"
      >
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke="rgba(255,255,255,0.15)"
          strokeWidth={SCORE_RING_STROKE}
          fill="rgba(0,0,0,0.4)"
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke={SCORE_RING_COLOR}
          strokeWidth={SCORE_RING_STROKE}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - (value / 100) * circumference}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-extrabold leading-none text-white">
          {value}
        </span>
        <span className="mt-0.5 text-[7px] text-gray-200">Position Score</span>
      </div>
    </div>
  );
}

function CardTile({ tile, item, toWholeScore }) {
  return (
    <div className="flex min-w-0 items-start gap-1.5">
      {tile.icon && (
        <FontAwesomeIcon
          icon={tile.icon}
          className="mt-0.5 h-4 w-4 shrink-0 text-gray-300"
        />
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate text-[8px] font-medium uppercase tracking-wide text-gray-300">
          {tile.title}
        </div>
        <div className="text-sm font-bold leading-tight text-white">
          {tile.format(item)}
        </div>
        {isPresent(item.score) && (
          <ScoreBar score={item.score} toWholeScore={toWholeScore} />
        )}
      </div>
    </div>
  );
}

function CardSection({
  title,
  tiles,
  items,
  columns,
  toWholeScore,
  className = "",
}) {
  const visibleTiles = tiles.filter((tile) => isPresent(items?.[tile.key]));
  if (visibleTiles.length === 0) return null;

  return (
    <section
      className={`rounded-lg border border-white/15 bg-black/50 px-3 py-2 ${className}`}
    >
      <h3 className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-white">
        {title}
      </h3>
      <div
        className="grid gap-x-3 gap-y-6"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {visibleTiles.map((tile) => (
          <CardTile
            key={tile.key}
            tile={tile}
            item={items[tile.key]}
            toWholeScore={toWholeScore}
          />
        ))}
      </div>
    </section>
  );
}

function VerifiedBadge() {
  return (
    <div className="absolute bottom-0 left-0 flex items-center gap-1.5 py-1 pl-4 pr-8 text-white">
      <span className="flex h-4 w-4 items-center justify-center rounded-full border-2 border-white text-[8px] font-extrabold leading-none">
        T
      </span>
      <span className="text-[11px] font-extrabold tracking-wide">
        TRACKIT VERIFIED
      </span>
    </div>
  );
}

const CardShell = forwardRef(function CardShell(
  { className = "", showTitle = true, children },
  ref,
) {
  return (
    <>
      {showTitle && (
        <div className="mb-3 text-sm font-medium text-gray-900">
          {" "}
          Card Preview{" "}
        </div>
      )}
      <div
        ref={ref}
        className={`relative min-h-[400px] w-[640px] overflow-hidden rounded-xl bg-[url('../../public/ScoreCard_bg.png')] bg-cover bg-center px-6 pb-10 pt-5 text-white ${className}`}
      >
        {children}
        <VerifiedBadge />
      </div>
    </>
  );
});

const PlayerCard = forwardRef(function PlayerCard(
  { card, empty = false, showTitle = true },
  ref,
) {
  if (empty) {
    return (
      <CardShell
        ref={ref}
        showTitle={showTitle}
        className="flex flex-col items-center justify-center text-center"
      >
        <p className="text-lg font-bold">Start building your profile card</p>
        <p className="mt-2 text-sm text-gray-300">
          Select the information you want to share from the options on the left.
        </p>
        <p className="mt-3 text-sm text-gray-300">
          Your card preview will appear here.
        </p>
      </CardShell>
    );
  }

  const hasSchoolLocation =
    isPresent(card?.school) ||
    isPresent(card?.state) ||
    isPresent(card?.graduationYear);
  const hasQrCode = isPresent(card?.profileLinkToken);

  return (
    <CardShell ref={ref} showTitle={showTitle}>
      <div className="flex items-start gap-4">
        {isPresent(card?.profilePhotoUrl) && (
          <img
            alt={card.firstName}
            src={card.profilePhotoUrl}
            className="h-20 w-20 shrink-0 rounded-md object-cover"
          />
        )}
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-bold uppercase leading-tight tracking-wide text-white">
            {card?.firstName} {card?.lastName}
          </h2>
          {isPresent(card?.position) && (
            <p className="text-base text-gray-100">{card.position}</p>
          )}
          {isPresent(card?.positionRank) && (
            <p className="text-base text-gray-100">
              #{card.positionRank} (Overall Position Rank)
            </p>
          )}
          {hasSchoolLocation && (
            <p className="text-base text-gray-100">
              {formatSchoolLocation(card)}
            </p>
          )}
        </div>
        {isPresent(card?.positionScore) && (
          <PositionScoreRing score={card.positionScore} />
        )}
      </div>

      <CardSection
        title="Measurements"
        tiles={MEASUREMENT_TILES}
        items={card?.measurements}
        columns={5}
        toWholeScore={Math.round}
        className="mt-3"
      />

      <div className="mt-2 flex items-end gap-3">
        <CardSection
          title="Athletic Drills"
          tiles={DRILL_TILES}
          items={card?.drills}
          columns={4}
          toWholeScore={Math.trunc}
          className="flex-1"
        />
        {hasQrCode && (
          <div className="ml-auto flex shrink-0 flex-col items-center">
            <div className="rounded-md bg-white p-1.5">
              <QRCodeCanvas
                value={`${process.env.REACT_APP_URL}/public-link/${card.profileLinkToken}`}
                size={QR_CODE_SIZE}
              />
            </div>
            <span className="mt-1 text-[10px] text-gray-200">
              View My Profile
            </span>
          </div>
        )}
      </div>
    </CardShell>
  );
});

export default PlayerCard;
